'use client';

import { memo } from 'react';
import { Box, Typography, Stack, Chip, Avatar, IconButton, Button, alpha } from '@mui/material';
import { Close, Business, SwapHoriz, Inventory2, Download } from '@mui/icons-material';
import { themeColors } from '../../../theme/theme';
import { formatDateTime } from '../../../utils/formatDateTime';

/**
 * Header section of the booking details sheet
 * Shows status, price, parcel ID, and action buttons
 */
const SheetHeader = memo(
  ({
    parcelId,
    status,
    statusColor,
    price,
    dateTime,
    isFirmTrip,
    isMultiStage,
    totalLegs,
    onClose,
    onDownloadInvoice,
  }) => {
    const isDelivered = status?.toUpperCase() === 'DELIVERED';

    return (
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${statusColor}15 0%, ${statusColor}05 100%)`,
          borderBottom: `2px solid ${statusColor}30`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Chip
              label={status?.replace(/_/g, ' ')}
              sx={{
                bgcolor: statusColor,
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                height: 32,
                px: 1.5,
              }}
            />
            {isFirmTrip && (
              <Chip
                icon={<Business sx={{ fontSize: 14, color: '#1976d2' }} />}
                label="Firm"
                size="small"
                sx={{
                  bgcolor: '#1976d215',
                  color: '#1976d2',
                  fontWeight: 600,
                  fontSize: 11,
                  height: 24,
                }}
              />
            )}
            {isMultiStage && (
              <Chip
                icon={<SwapHoriz sx={{ fontSize: 14, color: '#ff9800' }} />}
                label={`${totalLegs} Legs`}
                size="small"
                sx={{
                  bgcolor: '#ff980015',
                  color: '#ff9800',
                  fontWeight: 600,
                  fontSize: 11,
                  height: 24,
                }}
              />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            {price && (
              <Typography
                variant="h5"
                fontWeight={700}
                color={themeColors.primaryColor}
                sx={{ letterSpacing: '-0.5px' }}
              >
                ₹{price}
              </Typography>
            )}
            {isDelivered && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download sx={{ fontSize: 16 }} />}
                onClick={onDownloadInvoice}
                sx={{
                  borderColor: themeColors.primaryColor,
                  color: themeColors.primaryColor,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: themeColors.primaryColor,
                    bgcolor: alpha(themeColors.primaryColor, 0.05),
                  },
                }}
              >
                Invoice
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              bgcolor: `${statusColor}20`,
              color: statusColor,
              width: 40,
              height: 40,
            }}
          >
            <Inventory2 sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="body1" fontWeight={700}>
              #{parcelId}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {formatDateTime(dateTime)}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }
);

SheetHeader.displayName = 'SheetHeader';

export default SheetHeader;
